import React, { Component } from 'react';
import 'aframe';
import { ScreenReaderOnly } from '../../common/ADA';

import {
  ImageHotspot,
  TextHotspot,
  LinkHotspot,
  VideoHotspot,
  AudioHotspot,
  EmbedHotspot,
  HighlightHotspot,
  StopHotspot,
  PanoHotspot
} from '../MediaType/index';
import HotspotAssets from '../HotspotAssets/HotspotAssets';

import { buildImageFromId, buildAudioFromId } from '../../../utils/cloudinary';
import * as constants from '../../../constants';
import { buildCloudinaryURLFromMediaURL } from '../../../utils/cloudinary';

import { iconSizes } from '../hotspotConstants';

import { getTypeLabel } from "../../../utils/accessibilityHelpers";
import { HOTSPOT_LABEL_THEME } from '../../../constants';

class HotspotWrapper extends Component {
  constructor() {
    super();
    this.state = {
      activeHotspot: '',
      assets: {},
    };
  }

  componentDidMount() {
    this.scene = document.getElementById('scene');
    if (this.scene) {
      this.scene.addEventListener('hotspot-open', this.handleHotspotOpen);
    }
  }

  componentWillUnmount() {
    this.scene = document.getElementById('scene');
    if (this.scene) {
      this.scene.removeEventListener('hotspot-open', this.handleHotspotOpen);
    }
  }

  handleHotspotOpen = (event) => {
    const { hotspotId } = event.detail;
    if (hotspotId) {
      this.updateActiveHotspot(hotspotId, false, false);
    }
  };

  addAsset = (id, src, alt) => {
    this.setState(state => ({
      assets: {
        ...state.assets,
        [id]: {
          id: id,
          src: src,
          type: 'image',
          alt: alt || '',
          key: id,
          loaded: false,
        },
      },
    }));
  };

  handleAssetLoaded = (id, width, height) => {
    this.setState(state => ({
      assets: {
        ...state.assets,
        [id]: {
          ...state.assets[id],
          loaded: true,
          width,
          height,
        },
      },
    }));
  };

  renderHotspots = () => {
    const {
      audio,
      images,
      panos,
      hotspots,
      hotspotIcons = {},
      hotspotId: hotspotInEditId,
      history,
      isAddHotspot,
      changeStop,
      handlePlay,
      playing,
      tourAccountId,
      tourId,
      tourName,
      videos,
      toggleMainMediaStatus,
      isMainMediaPlaying,
      vrMode,
      showHotspotModal,
      tours,
      aid,
      isStatic,
      icons,
      hotspotLabelTheme = HOTSPOT_LABEL_THEME.DEFAULT,
      primaryColor,
    } = this.props;
    const _primaryColor =
      (tourId && tours && tours[tourId]?.styles?.primaryColor) ?? primaryColor;

    const { assets, activeHotspot } = this.state;

    return Object.values(hotspots).map(hotspot => {
      const {
        hotspotId,
        name,
        darkmode,
        outWorldDisplay: _outWorldDisplay,
        position: { position, rotation, camera_position },
        iconSize = iconSizes['medium'],
        labelTheme,
      } = hotspot;
      // static is always OOW
      const outWorldDisplay = isStatic || _outWorldDisplay;
      const currentHotspotLabelTheme = labelTheme ?? hotspotLabelTheme;
      const stringPosition =
        position && `${position.x} ${position.y} ${position.z}`;
      const stringRotation =
        rotation && `${rotation.x} ${rotation.y} ${rotation.z}`;
      const stringCamPosition =
        camera_position &&
        `${camera_position.x} ${camera_position.y} ${camera_position.z}`;

      const media = Object.values(hotspot.media)[0];

      const isDisabled = isAddHotspot && hotspotInEditId !== hotspotId;
      const isBeingEdited = isAddHotspot && !isDisabled;
      const showLabel =
        typeof hotspot.showLabel === 'boolean' ? hotspot.showLabel : true;
      const showBaseHotspot = !outWorldDisplay && !showHotspotModal;
      const iconSrc = icons
        ? buildCloudinaryURLFromMediaURL(icons[hotspot?.icon]?.url, 'auto')
        : null;
      const iconData = (icons && icons[hotspot?.icon]) || null;
      const ariaLabel = `${name} (${getTypeLabel(media.type)} hotspot)`;

      switch (media && media.type) {
        case constants.IMAGES: {
          // TODO: Same data from media library and hotspot upload
          const image = images[media.id] || media;
          if (image) {
            // image assets need to be put in a-assets
            const imageSrc = buildImageFromId(image.id, 'auto');
            return (
              <ImageHotspot
                active={activeHotspot === hotspotId}
                showBaseHotspot={showBaseHotspot}
                addAsset={this.addAsset}
                alt={image.alt}
                assets={assets}
                cameraPosition={stringCamPosition}
                contentSize={media.contentSize}
                darkMode={darkmode}
                showLabel={showLabel}
                hotspotId={hotspotId}
                isBeingEdited={isBeingEdited}
                isDisabled={isDisabled}
                key={hotspotId}
                mediaId={image.id}
                onClick={this.updateActiveHotspot}
                position={stringPosition}
                rotation={stringRotation}
                src={imageSrc}
                title={name}
                tourId={tourId}
                tourName={tourName}
                showHotspotModal={showHotspotModal}
                hideGlow={hotspotIcons['image']}
                aid={aid}
                hidden={vrMode && outWorldDisplay}
                iconSize={iconSize}
                outWorldDisplay={outWorldDisplay}
                icon={iconData}
                iconSrc={iconSrc}
                ariaLabel={ariaLabel}
                primaryColor={_primaryColor}
                hotspotLabelTheme={currentHotspotLabelTheme}
              />
            );
          }
          return null;
        }
        case constants.PANOS: {
          const pano = {
            ...(media || {}),
            ...(panos[media.id] || {}), // Use updates from media library (like alt text)
          }

          if (Object.keys(pano).length) {
            return (
              <PanoHotspot
                key={hotspotId}
                pano={pano}
                title={name}
                position={stringPosition}
                rotation={stringRotation}
                icon={iconData}
                iconSrc={iconSrc}
                iconSize={iconSize}
                hotspotId={hotspotId}
                cameraPosition={stringCamPosition}
                tourId={tourId}
                tourName={tourName}
                aid={aid}
                darkMode={darkmode}
                assets={assets}
                active={activeHotspot === hotspotId}
                showBaseHotspot={showBaseHotspot}
                isBeingEdited={isBeingEdited}
                isDisabled={isDisabled}
                showLabel={showLabel}
                hidden={vrMode && outWorldDisplay}
                hideGlow={hotspotIcons['pano']}
                primaryColor={_primaryColor}
                onClick={this.updateActiveHotspot}
                ariaLabel={ariaLabel}
                hotspotLabelTheme={currentHotspotLabelTheme}
              />
            );
          }
          return null;
        }
        case constants.TEXT: {
          return (
            <TextHotspot
              active={activeHotspot === hotspotId}
              showBaseHotspot={showBaseHotspot}
              cameraPosition={stringCamPosition}
              darkMode={darkmode}
              hotspotLabelTheme={currentHotspotLabelTheme}
              showLabel={showLabel}
              hotspotId={hotspotId}
              html={media.html}
              isBeingEdited={isBeingEdited}
              isDisabled={isDisabled}
              key={hotspotId}
              onClick={this.updateActiveHotspot}
              position={stringPosition}
              rotation={stringRotation}
              contentSize={media.contentSize}
              title={name}
              tourId={tourId}
              tourName={tourName}
              showHotspotModal={showHotspotModal}
              aid={aid}
              hidden={vrMode && outWorldDisplay}
              hideGlow={hotspotIcons['text']}
              iconSize={iconSize}
              isInEditor={isAddHotspot}
              outWorldDisplay={outWorldDisplay}
              icon={iconData}
              iconSrc={iconSrc}
              ariaLabel={ariaLabel}
              primaryColor={_primaryColor}
            />
          );
        }
        case constants.URL: {
          return (
            <LinkHotspot
              active={activeHotspot === hotspotId}
              key={hotspotId}
              position={stringPosition}
              rotation={stringRotation}
              cameraPosition={stringCamPosition}
              title={name}
              hotspotId={hotspotId}
              history={history}
              isBeingEdited={isBeingEdited}
              isDisabled={isDisabled}
              changeStop={changeStop}
              handlePlay={handlePlay}
              playing={playing}
              onClick={this.updateActiveHotspot}
              showLabel={showLabel}
              darkMode={darkmode}
              hidden={vrMode && media.linkType !== 'stop'}
              tourId={tourId}
              tourName={tourName}
              aid={aid}
              hideGlow={hotspotIcons[media.linkType]}
              iconSize={iconSize}
              icon={iconData}
              iconSrc={iconSrc}
              ariaLabel={ariaLabel}
              primaryColor={_primaryColor}
              hotspotLabelTheme={currentHotspotLabelTheme}
              {...media}
            />
          );
        }
        case constants.AUDIO: {
          const audioTrack = audio[media.id] || media;
          if (audioTrack) {
            return (
              <AudioHotspot
                active={activeHotspot === hotspotId}
                showBaseHotspot={showBaseHotspot}
                contentSize={media.contentSize}
                id={audioTrack.id}
                key={hotspotId}
                position={stringPosition}
                rotation={stringRotation}
                cameraPosition={stringCamPosition}
                title={name}
                transcript={audioTrack.transcript}
                hotspotId={hotspotId}
                isBeingEdited={isBeingEdited}
                isDisabled={isDisabled}
                details={media.details}
                history={history}
                changeStop={changeStop}
                handlePlay={handlePlay}
                playing={playing}
                onClick={this.updateActiveHotspot}
                darkMode={hotspot.darkmode}
                showLabel={showLabel}
                src={buildAudioFromId(audioTrack.id)}
                tourId={tourId}
                tourName={tourName}
                toggleMainMediaStatus={toggleMainMediaStatus}
                isMainMediaPlaying={isMainMediaPlaying}
                showHotspotModal={showHotspotModal}
                primaryColor={_primaryColor}
                media={media}
                aid={aid}
                hideGlow={hotspotIcons['audio']}
                iconSize={iconSize}
                outWorldDisplay={outWorldDisplay}
                icon={iconData}
                iconSrc={iconSrc}
                ariaLabel={ariaLabel}
                hotspotLabelTheme={currentHotspotLabelTheme}
              />
            );
          }
          return null;
        }
        case constants.VIDEOS: {
          const video = videos[media.id] || media;
          if (video) {
            return (
              <VideoHotspot
                active={activeHotspot === hotspotId}
                showBaseHotspot={showBaseHotspot}
                key={hotspotId}
                position={stringPosition}
                rotation={stringRotation}
                cameraPosition={stringCamPosition}
                title={name}
                hotspotId={hotspotId}
                isBeingEdited={isBeingEdited}
                isDisabled={isDisabled}
                video={video}
                onClick={this.updateActiveHotspot}
                handlePlay={handlePlay}
                playing={playing}
                darkMode={darkmode}
                showLabel={showLabel}
                tourAccountId={tourAccountId}
                tourId={tourId}
                tourName={tourName}
                assets={assets}
                addAsset={this.addAsset}
                toggleMainMediaStatus={toggleMainMediaStatus}
                hidden={vrMode}
                showHotspotModal={showHotspotModal}
                aid={aid}
                hideGlow={hotspotIcons['video']}
                iconSize={iconSize}
                outWorldDisplay={outWorldDisplay}
                icon={iconData}
                iconSrc={iconSrc}
                ariaLabel={ariaLabel}
                primaryColor={_primaryColor}
                hotspotLabelTheme={currentHotspotLabelTheme}
              />
            );
          }
          return null;
        }
        case constants.EMBED: {
          return (
            <EmbedHotspot
              hotspotId={hotspotId}
              rotation={stringRotation}
              active={activeHotspot === hotspotId}
              position={stringPosition}
              aid={aid}
              iconSize={iconSize}
              tourName={tourName}
              tourId={tourId}
              isDisabled={isDisabled}
              title={name}
              onClick={this.updateActiveHotspot}
              isBeingEdited={isBeingEdited}
              key={hotspotId}
              showBaseHotspot={showBaseHotspot}
              showHotspotModal={showHotspotModal}
              showLabel={showLabel}
              cameraPosition={stringCamPosition}
              darkMode={darkmode}
              hideGlow={hotspotIcons['embed']}
              embedText={media.html}
              icon={iconData}
              iconSrc={iconSrc}
              ariaLabel={ariaLabel}
              primaryColor={_primaryColor}
              hotspotLabelTheme={currentHotspotLabelTheme}
            />
          );
        }
        case constants.HIGHLIGHT: {
          return (
            <HighlightHotspot
              hotspotId={hotspotId}
              rotation={stringRotation}
              active={activeHotspot === hotspotId}
              position={stringPosition}
              aid={aid}
              iconSize={iconSize}
              tourName={tourName}
              tourId={tourId}
              isDisabled={isDisabled}
              title={name}
              onClick={this.updateActiveHotspot}
              isBeingEdited={isBeingEdited}
              key={hotspotId}
              showBaseHotspot={showBaseHotspot}
              showHotspotModal={showHotspotModal}
              showLabel={showLabel}
              cameraPosition={stringCamPosition}
              darkMode={darkmode}
              hideGlow={hotspotIcons['highlight']}
              icon={iconData}
              iconSrc={iconSrc}
              primaryColor={_primaryColor}
              hotspotLabelTheme={currentHotspotLabelTheme}
            />
          );
        }
        case constants.STOP: {
          return (
            <StopHotspot
              active={activeHotspot === hotspotId}
              changeStop={changeStop}
              darkMode={darkmode}
              handlePlay={handlePlay}
              hidden={vrMode && media.linkType !== 'stop'}
              hideGlow={hotspotIcons[media.linkType]}
              hotspotId={hotspotId}
              icon={iconData}
              iconSize={iconSize}
              iconSrc={iconSrc}
              isBeingEdited={isBeingEdited}
              isDisabled={isDisabled}
              key={hotspotId}
              onClick={this.updateActiveHotspot}
              playing={playing}
              position={stringPosition}
              rotation={stringRotation}
              showLabel={showLabel}
              title={name}
              tourId={tourId}
              tourName={tourName}
              ariaLabel={ariaLabel}
              cameraPosition={stringCamPosition}
              primaryColor={_primaryColor}
              hotspotLabelTheme={currentHotspotLabelTheme}
              {...media}
            />
          );
        }
        default: {
          return null;
        }
      }
    });
  };

  // required for iOS Voiceover
  renderHiddenAdaHotspots = () => {
    const { hotspots, isStatic } = this.props;

    if (!AFRAME.utils.device.isMobile()) {
      return null;
    }

    const getIcon = (hotspotId) =>
      document.getElementById(`icon_${hotspotId}`) ||
      document.getElementById(`custom-icon_${hotspotId}`);

    const getContent = (hotspotId) =>
      document.getElementById(`content_${hotspotId}`);

    return Object.values(hotspots).map(({ hotspotId, name, media }) => {
      const id = `hidden-ada-hotspot-${hotspotId}`;
      return (
        <ScreenReaderOnly
          key={id}
          id={id}
          role="button"
          tabIndex={0}
          aria-label={`Hotspot: ${name}`}
          onFocus={() => {
            const icon = getIcon(hotspotId);
            !isStatic && icon?.emit('rotateToHotspot');
            icon?.emit('mouseenter');
            icon?.emit('focus');
          }}
          onBlur={() => {
            const icon = getIcon(hotspotId);
            const content = getContent(hotspotId);
            icon?.emit('mouseleave');
            icon?.emit('focusout');
            if (content?.getAttribute('visible') === true && hotspotId) {
              icon?.emit('click');
            }
          }}
          onClick={() => {
            this.updateActiveHotspot();
            getIcon(hotspotId)?.emit('click');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.updateActiveHotspot();
              getIcon(hotspotId)?.emit('click');
            }
          }}
        >
          Hotspot: {name}
        </ScreenReaderOnly>
      );
    });
  };

  updateActiveHotspot = (hotspotId, disabled, isLink) => {
    const { setPauseAutoRotate } = this.props;
    const { activeHotspot } = this.state;
    if (!disabled && activeHotspot !== hotspotId) {
      this.setState({ activeHotspot: hotspotId });
      if (typeof setPauseAutoRotate === 'function') {
        if (isLink) setPauseAutoRotate(false);
        else setPauseAutoRotate(true);
      }
    } else if (activeHotspot === hotspotId) {
      this.setState({ activeHotspot: '' });
      if (typeof setPauseAutoRotate === 'function') setPauseAutoRotate(false);
    }
  };

  render() {
    const { hotspotIcons, media = {}, isPublic } = this.props;
    const { assets } = this.state;
    const renderHotspots = isPublic
      ? true
      : media.mediaFetching === 'fetch_success' && media.mediaFetched;

    return (
      <React.Fragment>
        {renderHotspots ? (
          <React.Fragment>
            <HotspotAssets
              hotspotIcons={hotspotIcons}
              assets={assets}
              handleAssetLoaded={this.handleAssetLoaded}
            />
            {this.renderHiddenAdaHotspots()}
            {this.renderHotspots()}
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }
}

export default HotspotWrapper;
